import useSystemDateTheme from "../../../hooks/useSystemDateTheme";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {useCookieContext} from "../../../contexts";
import {useRef, useState} from "react";
import {
    EvaluateProgramUpdateRef
} from "../orders/OrderView/Tabs/OrderInfoTab/components/Updates/useProgramOrderUpdateActions";
import {ApproveByCenterManagerSubmitHandlerRef} from "../orders/OrderView/PageActions/useCloseRequestActions";
import PageHeaders, {ReceiptModalProps} from "../orders/OrderView/PageHeaders";
import PageWithTabs from "../../../components/PageWithTabs";
import {myWorkStatusClassNames} from "../researcherOrders/MyUnsignedWork/MyUnsignedWorkGridView";
import {OrderStatus} from "../../../shared/lookups";
import PageActions from "../orders/OrderView/PageActions";
import {OrderInfoTab} from "../orders/OrderView/Tabs/OrderInfoTab";
import AboutProgramTab from "../programs/ViewProgram/tabs/AboutProgramTab";
import ProgramMinutesTab from "../orders/OrderView/Tabs/ProgramMinutesTab";
import SpeechesTab from "../orders/OrderView/Tabs/SpeechesTab";
import AdditionalAttachmentsTab from "../orders/OrderView/Tabs/AdditionalAttachmentsTab";
import SubProceduresTab from "../orders/OrderView/Tabs/SubProceduresTab";
import ReturnOrderToReviewerModal from "../orders/OrderView/OrderTaskActionModals/ReturnOrderToReviewerModal";
import SendOrderToFundingUnitModal from "../orders/OrderView/OrderTaskActionModals/SendOrderToFundingUnitModal";
import ResearcherCloseRequestPage from "../orders/OrderView/ResearcherCloseRequestPage";
import Modal from "../../../components/blocks/Modal";
import {ResearcherPaymentLocalResult} from "../../../services/researcherPayments/models/ResearcherPaymentsType";
import AttachmentCard from "../../../components/blocks/AttachmentCard";
import {FormTabs, OrderActivePages, OrderUserTypes, tabsIds, TapsIds} from "../orders/OrderView";
import useCompletedOrderView from "./useCompletedOrderView";

const ExaminerCompletedOrder = ({
                       userType,
                       withGenericActions,
                   }: {
    userType: keyof typeof OrderUserTypes;
    withGenericActions?: boolean;
}) => {
    const { toSystemThemeDateFormat } = useSystemDateTheme();
    const { t } = useTranslation(["Orders", "Shared"]);
    const { serialGID } = useParams();
    const { isAr } = useCookieContext();
    const programUpdateHandlersRef = useRef<EvaluateProgramUpdateRef>(null);
    const approveByCenterManagerSubmitHandlerRef = useRef<ApproveByCenterManagerSubmitHandlerRef>(null);

    const { loading, openedTask, order, programOrderTaskState, orderProgram, setTriggerOrderUpdate } = useCompletedOrderView({
        serialGID,
        userType
    });

    const [activePage, setActivePage] = useState<keyof typeof OrderActivePages>(
        userType !== OrderUserTypes.ResearcherSendCloseResponseAfterReturnRequest ? "Default" : "ResearchCloseRequest",
    );
    const [showModalProps, setShowModalProps] = useState<ReceiptModalProps>({
        isOpen: false,
        type: "Receipt",
        data: [] as any,
    });
    const [modalProps, setModalProps] = useState<{
        orderId?: number;
        isOpen: boolean;
        type?: "return" | "respond"; // TODO: 2 types of return - check figma before and after sending to reviewers
    }>({
        orderId: undefined,
        isOpen: false,
        type: undefined,
    });

    const tapsFormRefs = useRef<{
        [key in FormTabs]: React.RefObject<HTMLFormElement>;
    }>({
        additionalAttachmentsTab: useRef<HTMLFormElement>(null),
        programMinutesTab: useRef<HTMLFormElement>(null),
        speechesTab: useRef<HTMLFormElement>(null),
    });

    const onSave = (selectedTabId: TapsIds) => {
        const form = tapsFormRefs.current![selectedTabId as FormTabs]?.current;
        if (form) {
            form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
            form.classList.add("was-validated");
        }
    };

    const onActionClicked = (action: "return" | "respond") => {
        setModalProps({ isOpen: true, type: action, orderId: programOrderTaskState!.orderId });
    };

    return (
        <>
            <PageWithTabs
                title={
                    programOrderTaskState ? (
                        orderProgram?.programDetails.find((x) => x.locale === (isAr ? "Ar" : "En"))?.displayName
                    ) : (
                        <>
                            {t("order")} <span dir="ltr">{order?.serialNumber ?? ""}</span>
                        </>
                    )
                }
                subTitle={
                    programOrderTaskState
                        ? `${t("programNumber")}: ${orderProgram?.serialNumber ?? ""}`
                        : `${t("appliedOn")}: ${toSystemThemeDateFormat(order?.createdOn)}`
                }
                defaultTabId={tabsIds.orderInfoTab}
                loading={loading}
                pageHeaders={PageHeaders({
                    order: order,
                    programOrderTaskState,
                    setShowModalProps,
                })}
                wrapperClassName={activePage === OrderActivePages.Default ? "" : "d-none"}
                actions={(selectedTabId) => (
                    <>
                        <div className={"d-flex align-items-center"}>
              <span
                  className={`badge rounded-4 ${
                      myWorkStatusClassNames[order?.orderStatusId ?? OrderStatus.Inprogress] as string
                  } py-2`}
              >
                {order?.orderStatus}
              </span>
                        </div>
                        <PageActions
                            order={order}
                            onActionClicked={onActionClicked}
                            openedTask={null}
                            onSave={onSave}
                            programOrderTaskState={programOrderTaskState}
                            selectedTabId={selectedTabId}
                            userType={userType}
                            programOrderId={programOrderTaskState?.orderId ?? 0}
                            serialGuid={serialGID ?? ""}
                            programUpdateHandlersRef={programUpdateHandlersRef}
                            approveByCenterManagerSubmitHandlerRef={approveByCenterManagerSubmitHandlerRef}
                            program={orderProgram}
                            setTriggerOrderUpdate={setTriggerOrderUpdate}
                            withGenericActions={withGenericActions}
                        />
                    </>
                )}
                tabs={[
                    {
                        id: tabsIds.orderInfoTab,
                        title: t("orderInfo"),
                        content: (
                            <OrderInfoTab
                                order={order}
                                orderProgram={orderProgram}
                                userType={userType}
                                activePage={activePage}
                                setActivePage={setActivePage}
                                openedTask={null}
                                programUpdateHandlersRef={programUpdateHandlersRef}
                                approveByCenterManagerSubmitHandlerRef={approveByCenterManagerSubmitHandlerRef}
                                setTriggerOrderUpdate={setTriggerOrderUpdate}
                            />
                        ),
                    },
                    ...(OrderUserTypes.Researcher.includes("Researcher")
                        ? [
                            {
                                id: tabsIds.aboutProgram,
                                title: t("aboutProgram"),
                                content: orderProgram && <AboutProgramTab program={orderProgram} />,
                            },
                        ]
                        : [
                            {
                                id: tabsIds.programMinutesTab,
                                title: t("programMinutes"),
                                content: (
                                    <ProgramMinutesTab
                                        ref={tapsFormRefs.current[tabsIds.programMinutesTab as FormTabs]}
                                        programOrderId={order?.id ?? 0}
                                    />
                                ),
                            },
                            {
                                id: tabsIds.speechesTab,
                                title: t("speeches"),
                                content: (
                                    <SpeechesTab
                                        ref={tapsFormRefs.current[tabsIds.speechesTab as FormTabs]}
                                        programOrderId={order?.id ?? 0}
                                    />
                                ),
                            },
                            {
                                id: tabsIds.additionalAttachmentsTab,
                                title: t("additionalAttachments"),
                                content: (
                                    <AdditionalAttachmentsTab
                                        programOrderId={order?.id ?? 0}
                                        ref={tapsFormRefs.current[tabsIds.additionalAttachmentsTab as FormTabs]}
                                    />
                                ),
                            },
                            {
                                id: tabsIds.subProceduresTab,
                                title: t("subProcedures"),
                                content: <SubProceduresTab orderId={order?.id ?? 0} />,
                            },
                        ]),
                ]}
            />

            <ReturnOrderToReviewerModal
                isOpen={modalProps.isOpen && modalProps.type === "return"}
                isInspectMode={order?.isInspectMode}
                onClose={() => {
                    setModalProps({ isOpen: false });
                }}
                orderId={modalProps.orderId ?? 0}
                activityGuid={openedTask?.data.activityGuid}
                serialGuid={serialGID}
            />

            <SendOrderToFundingUnitModal
                isOpen={modalProps.isOpen && modalProps.type === "respond"}
                onClose={() => {
                    setModalProps({ isOpen: false });
                }}
                orderId={modalProps.orderId ?? 0}
                serialGuid={serialGID}
                activityGuid={openedTask?.data.activityGuid}
                userType={userType}
            />

            {activePage === OrderActivePages.ResearchCloseRequest && (
                <ResearcherCloseRequestPage
                    userType={userType}
                    activePage={activePage}
                    order={order}
                    orderProgram={orderProgram}
                    setActivePage={setActivePage}
                    openedTask={null}
                    evaluateProgramUpdateRef={programUpdateHandlersRef}
                    serialGuid={serialGID ?? ""}
                    approveByCenterManagerSubmitHandlerRef={approveByCenterManagerSubmitHandlerRef}
                />
            )}

            <Modal
                containerClassName="bg-white pb-2"
                isOpen={showModalProps.isOpen}
                onClose={() => {
                    setShowModalProps({ isOpen: false, type: "Contract", data: [] as any });
                }}
                title={t("Common:attachments")}
                withHeaderBorder
                size="lg"
                titleClassName="fs-6 text-dark"
            >
                {showModalProps.type === "Contract" ? (
                    <div
                        dangerouslySetInnerHTML={{
                            __html:
                                showModalProps.data?.pdfTemplateDetails?.find(
                                    (pdf) => pdf.locale?.toLowerCase() == (isAr ? "ar" : "en"),
                                )?.templateContent ?? "",
                        }}
                    />
                ) : (
                    <div className="d-flex gap-2 flex-wrap">
                        {showModalProps.data.length
                            ? showModalProps.data.map((file: ResearcherPaymentLocalResult, index: number) => (
                                <AttachmentCard
                                    attachment={{
                                        extension: file.receipt?.extension ?? "",
                                        fileName: file.receipt?.name ?? "",
                                        id: file.receipt?.id ?? "",
                                        size: file.receipt?.size ?? "",
                                    }}
                                />
                            ))
                            : t("Common:noData")}
                    </div>
                )}
            </Modal>
        </>
    );
};

export default ExaminerCompletedOrder;